import * as React from "react";
import { useRadio } from "@react-aria/radio";
import { VisuallyHidden } from "@react-aria/visually-hidden";
import mergeRefs from "merge-refs";
import { css, cx } from "@nju/css";
import { useRadioGroupContext } from "./context";

export interface RadioProps {
  value: string;
  name?: string;
  space?: "default" | "narrow";
  isRequired?: boolean;
  isReadOnly?: boolean;
  isDisabled?: boolean;
  "aria-label"?: string;
  id?: string;
  labelId?: string;
  className?: string;
  hasMargin?: boolean;
}

export const Radio = React.memo(
  React.forwardRef<HTMLInputElement, React.PropsWithChildren<RadioProps>>(
    function Radio(props, ref) {
      const innerRef = React.useRef<HTMLInputElement>(null);

      const radioGroupProps = useRadioGroupContext();
      const { validationState, ...state } = radioGroupProps;

      const { inputProps } = useRadio(
        { ...props, ...radioGroupProps },
        state,
        innerRef,
      );

      const isSelected = state.selectedValue === props.value;
      const hasMargin = props.hasMargin ?? true;

      return (
        <label
          className={cx("flex items-center", props.className, {
            "mb-0": !hasMargin,
            "mb-4": hasMargin,
          })}
          id={props.labelId}
        >
          <VisuallyHidden>
            <input
              {...inputProps}
              name={props.name}
              ref={mergeRefs(ref, innerRef)}
            />
          </VisuallyHidden>
          <div
            className={styles.border({
              checked: isSelected,
              disabled: state.isDisabled,
              space: props.space,
              state: validationState,
            })}
          ></div>
          {props.children}
        </label>
      );
    },
  ),
);

const styles = {
  border: css({
    position: "relative",
    "@onlyMobile": {
      marginRight: 10,
    },
    flex: "0 0 20px",
    height: 20,
    borderRadius: "$round",
    transition: "$border",
    willChange: "border",
    defaultVariants: { checked: false, disabled: false, space: "default" },
    variants: {
      checked: {
        true: { border: "5px solid $colors$daintree" },
        false: { border: "2px solid $colors$geyser" },
      },
      disabled: {
        true: { cursor: "not-allowed" },
        false: { cursor: "pointer" },
      },
      state: {
        error: {
          borderColor: "$tiaMaria",
        },
      },
      space: {
        narrow: {
          marginRight: 10,
        },
        default: {
          marginRight: 20,
        },
      },
    },
  }),
};
